import { Modal, type ModalRootProps } from "@heroui/react";
import {
  type AppModalSize,
  type AppModalScroll,
  type AppModalVariant,
} from "./variants";

// ─── AppModal Root ───────────────────────────────────────────────────────────

/**
 * Constrained modal root props.
 * Modal root is a logical wrapper (DialogTrigger) — no DOM element rendered.
 */
export type AppModalProps = Omit<ModalRootProps, "className">;

// ─── AppModal.Backdrop ───────────────────────────────────────────────────────

type ModalBackdropProps = Parameters<typeof Modal.Backdrop>[0];

/**
 * Constrained backdrop props.
 * className escape hatch available for one-off overrides.
 */
export type AppModalBackdropProps = Omit<
  ModalBackdropProps,
  "className" | "variant"
> & {
  /** Backdrop visual variant. @default "opaque" */
  variant?: AppModalVariant;
  /** Optional className escape hatch for one-off overrides. */
  className?: string;
};

// ─── AppModal.Container ──────────────────────────────────────────────────────

type ModalContainerProps = Parameters<typeof Modal.Container>[0];

/**
 * Constrained container props.
 * className escape hatch available for one-off overrides.
 */
export type AppModalContainerProps = Omit<
  ModalContainerProps,
  "className" | "size" | "scroll"
> & {
  /** Dialog size. @default "md" */
  size?: AppModalSize;
  /** Scroll behavior. @default "inside" */
  scroll?: AppModalScroll;
  /** Optional className escape hatch for one-off overrides. */
  className?: string;
};

// ─── Components ──────────────────────────────────────────────────────────────

/**
 * AppModal root — logical wrapper for the dialog trigger.
 * Does not render a DOM element; provides context for sub-components.
 */
function AppModalRoot(props: AppModalProps) {
  return <Modal {...props} />;
}

/**
 * AppModal.Backdrop — constrained overlay behind the dialog.
 */
function AppModalBackdrop({
  variant = "opaque",
  className,
  ...props
}: AppModalBackdropProps) {
  return <Modal.Backdrop {...props} variant={variant} className={className} />;
}

/**
 * AppModal.Container — constrained dialog positioning container.
 */
function AppModalContainer({
  size = "md",
  scroll = "inside",
  className,
  ...props
}: AppModalContainerProps) {
  return <Modal.Container {...props} size={size} scroll={scroll} className={className} />;
}

/**
 * AppModal — Design system modal / dialog.
 *
 * Wraps HeroUI v3 Modal with constrained variants on Backdrop and Container.
 * Other sub-components (Dialog, Header, Heading, Body, Footer, CloseTrigger)
 * are HeroUI pass-throughs.
 *
 * @example
 * ```tsx
 * <AppModal>
 *   <AppModal.Backdrop isOpen={state.isOpen} onOpenChange={state.setOpen}>
 *     <AppModal.Container size="md">
 *       <AppModal.Dialog>
 *         {({ close }) => (
 *           <>
 *             <AppModal.Header>
 *               <AppModal.Heading>Confirm</AppModal.Heading>
 *             </AppModal.Header>
 *             <AppModal.Body>Are you sure?</AppModal.Body>
 *             <AppModal.Footer>
 *               <AppButton variant="ghost" onPress={close}>Cancel</AppButton>
 *               <AppButton variant="primary" onPress={close}>Confirm</AppButton>
 *             </AppModal.Footer>
 *           </>
 *         )}
 *       </AppModal.Dialog>
 *     </AppModal.Container>
 *   </AppModal.Backdrop>
 * </AppModal>
 * ```
 */
export const AppModal = Object.assign(AppModalRoot, {
  Backdrop: AppModalBackdrop,
  Container: AppModalContainer,
  Dialog: Modal.Dialog,
  Header: Modal.Header,
  Heading: Modal.Heading,
  Body: Modal.Body,
  Footer: Modal.Footer,
  CloseTrigger: Modal.CloseTrigger,
  Icon: Modal.Icon,
  Trigger: Modal.Trigger,
});
